import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import Card, { getEntitiesPageKpisCard } from "@/_components/Card"
import CustomButton from "@/_components/CustomButton"
import EntitiesButtons from "@/_components/EntitiesButtons"
import PersonLink from "@/_components/PersonLink"
import Table, { mergeColumns } from "@/_components/Table"
import { getEntityMapFunction, handleEntitiesPageRowClick, handleEntitiesPageSelectAll } from "@/_services/entity"
import { loc } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
import { getPayerPayeeValues } from "@/_services/personUtils"
import { fetchPinnedEntities, getConfigAtPath, getPageConfig } from "@/_services/userConfiguration"
import {
  applyPageAdvancedSearchConfig,
  applyPageConfigFields,
  checkOrganizationNameInclude,
  downloadFileFromResponse,
  getClientRouteKey,
  getEntities,
  hasQueryParamsChanged,
  mergeQueryParams,
  personRoles,
  searchEntities,
  searchParamToObject,
} from "@/_services/utils"
import axios from "axios"
import { frontScripts, pruneObject, toQueryString, truncate, YYYYMMDD } from "basikon-common-utils"
import React, { Suspense } from "react"
import { Grid } from "react-bootstrap"

const BillingModal = React.lazy(() => import("@/billing/BillingModal.jsx"))

class InvoicesPage extends React.Component {
  constructor(props) {
    super(props)
    const {
      location: { pathname: clientRoute },
    } = props

    const pageConfigKey = getClientRouteKey(clientRoute) || "InvoicesPage"
    const pageConfig = getPageConfig(pageConfigKey)
    const configColumns = pageConfig?.columns
    const defaultColumns = [
      { title: "Registration", name: "registration", linkTo: "/invoice/{registration}" },
      { title: "Organization", name: "organization.name", hidden: true },
      { title: "Status", name: "status", select: "invoiceStatus", badge: true },
      { title: "Name", name: "name" },
      { title: "Invoice number", name: "invoiceNumber" },
      { title: "Payable/Receivable", name: "pr", select: "invoicePr" },
      { title: "Type", name: "type", select: "invoiceType" },
      { title: "Payment mode", name: "paymentMode", select: "paymentMode" },
      { title: "Contract", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
      { title: "Payer", name: "payer", sortableOnServer: false },
      { title: "Payee", name: "payee", sortableOnServer: false },
      { title: "Person", name: "payerOrPayee", hidden: true, sortableOnServer: false },
      { title: "Amount (excl. tax)", name: "amountExclTax", type: "currency", currencyPath: "currency", excelFormat: "currency" },
      { title: "Amount (incl. tax)", name: "amountInclTax", type: "currency", currencyPath: "currency", excelFormat: "currency" },
      { title: "Due date", name: "dueDate", type: "date" },
      { title: "Payment date", name: "paymentDate", type: "date" },
      { title: "Internal reference", name: "internalReference", hidden: true },
      { title: "External reference", name: "externalReference", hidden: true },
      { title: "User", name: "_insertUser", hidden: true },
      { title: "Date", name: "_updateDate", type: "date", hidden: true },
      { title: <>&#8205;</>, name: "actions", sortable: false, hidden: true },
    ]
    const columns = mergeColumns(defaultColumns, configColumns)

    this.state = {
      invoices: [],
      entityName: "Invoice",
      pinnedData: [],
      showBillingModal: false,
      billingLoading: false,
      selectedEntities: new Set(),
      transitions: [],
      transitionsLoading: false,
      pageConfigKey,
      pageConfig,
      columns,
      filterFunction: getEntityMapFunction(columns),
      isServerSearch: false,
      paginationParams: null,
    }

    this.preventAutoRefresh = null
  }

  componentDidMount() {
    const { pageConfigKey } = this.state
    this.getInvoices()

    applyPageAdvancedSearchConfig(pageConfigKey, invoiceSearchFields)
  }

  componentDidUpdate(prevProps) {
    if (!this.preventAutoRefresh && hasQueryParamsChanged(this.props, prevProps)) {
      this.getInvoices()
    }
  }

  getInvoices = async ({ params, isAdvancedSearch, nextPagination } = {}) => {
    const { location, history } = this.props
    const { pageConfig, columns, entityName, invoices: stateEntities } = this.state

    const queryParams = { ...searchParamToObject(location.search), ...(params || {}) }
    const getEntitiesQueryParams = {
      ...pageConfig?.defaultQuery,
      ...pruneObject(queryParams),
      ...(pageConfig?.query || {}),
      projection: pageConfig?.query?.projection || pageConfig?.projection,
    }
    getEntitiesQueryParams.include = checkOrganizationNameInclude(columns, getEntitiesQueryParams.include)
    if (getEntitiesQueryParams.projection && !getEntitiesQueryParams.projection.includes("timeZone")) getEntitiesQueryParams.projection += ",timeZone"

    this.setState({ loading: true })

    if (nextPagination) {
      const { data: moreEntities, paginationParams } = await getEntities(entityName, getEntitiesQueryParams, { nextPagination })
      this.setState({ invoices: [...stateEntities, ...applyPageConfigFields(pageConfig, moreEntities)], paginationParams, loading: false })
      this.preventAutoRefresh = true
      delete queryParams.range
      history.replace(`${location.pathname}${toQueryString(queryParams)}`)
      setTimeout(() => {
        this.preventAutoRefresh = false
      })
      return
    }

    const [{ data: invoices, paginationParams }, pinnedData] = await Promise.all([
      getEntities(entityName, getEntitiesQueryParams),
      fetchPinnedEntities({ entityName, queryParams: getEntitiesQueryParams }),
    ])

    this.setState({
      invoices: applyPageConfigFields(pageConfig, invoices),
      paginationParams,
      pinnedData: this.formatTableData(applyPageConfigFields(pageConfig, pinnedData)),
      loading: false,
      selectedEntities: new Set(), // Reset selected entities after getting new entities
      isServerSearch: !!getEntitiesQueryParams.search,
    })

    if (params) {
      this.preventAutoRefresh = true
      if (isAdvancedSearch) queryParams.page = 1
      history.replace(`${location.pathname}${mergeQueryParams(location.search, queryParams)}`)
      setTimeout(() => {
        this.preventAutoRefresh = false
      })
    }
  }

  handleExecuteBilling = async form => {
    this.setState({ billingLoading: true })
    const useDateNoTime = getConfigAtPath("useDateNoTime")

    try {
      const orgaFilter = form.organizations
        ? `&organizations=${typeof form.organizations === "string" ? form.organizations : form.organizations.join(",")}`
        : ""
      const result = (
        await axios.get(
          `/api/script/runs/${frontScripts.runBilling}?date=${useDateNoTime ? YYYYMMDD(form.date) : form.date.toISOString()}${orgaFilter}`,
        )
      ).data
      if (Array.isArray(result)) {
        // old style run-billing
        const invoices = result
        addNotification(loc`${invoices.length} invoice(s) created`)
        if (invoices.length > 0) this.getInvoices(true)
      } else {
        // new style: it's a batch
        const batch = result
        addNotification(loc`${batch.nbTotal || 0} invoice(s) created`)
        if (batch.nbTotal > 0) this.getInvoices(true)
      }
    } catch (error) {
      addOops(error)
    }

    this.setState({ billingLoading: false })
  }

  handleInvoiceDownload = async (e, invoice) => {
    e.preventDefault()
    let response = await axios.get(`/api/billing/invoices/${invoice.registration}/documents/INVOICE/content`, { responseType: "blob" })
    if (response) downloadFileFromResponse(response)
  }

  handleBillingModalClose = form => {
    if (form?.date) {
      this.handleExecuteBilling(form)
    }
    this.setState({ showBillingModal: false })
  }

  formatTableData = data => {
    return data.map(invoice => {
      let { persons = [] } = invoice

      const payer = persons.find(p => p.role === personRoles.PAYER) || {}
      const payee = persons.find(p => p.role === personRoles.PAYEE) || {}

      const payerLink = <PersonLink registration={payer.personRegistration}>{truncate(payer.name, 17) || payer.personRegistration}</PersonLink>
      const payeeLink = <PersonLink registration={payee.personRegistration}>{truncate(payee.name, 17) || payee.personRegistration}</PersonLink>

      return {
        ...invoice,
        id: invoice.registration,
        payer: payerLink,
        payee: payeeLink,
        payerOrPayee: invoice.pr === "R" ? payerLink : payeeLink,
        totalBalance: invoice.totalBalance ?? invoice.amountInclTax,
        actions: <ButtonWithTooltip className="icn-pdf-download icn-xs" onClick={e => this.handleInvoiceDownload(e, invoice)} tooltip="Download" />,
      }
    })
  }

  render() {
    const {
      entityName,
      invoices = [],
      selectedEntities,
      transitions = [],
      loading,
      billingLoading,
      transitionsLoading,
      showBillingModal,
      pageConfig,
      columns,
      filterFunction,
      pinnedData,
      isServerSearch,
      paginationParams,
    } = this.state
    const { location, history } = this.props

    const title = loc(pageConfig?.title || "Invoices")
    const queryParams = searchParamToObject(location.search)

    const { filter = "" } = queryParams
    const { entities: filteredInvoices, hasSearchResultsOnHiddenFields } = filter?.trim()
      ? filterFunction(invoices, filter, { isServerSearch })
      : { entities: invoices }

    const action = selectedEntities.size ? (
      <EntitiesButtons
        entities={invoices}
        entityName={entityName}
        transitions={transitions}
        getEntities={this.getInvoices}
        selectedEntities={selectedEntities}
        transitionsLoading={transitionsLoading}
        customButtons={pageConfig?.selectionButtons}
        transitionsButton={pageConfig?.transitionsButton}
        handleSelectAll={async selectedEntities => await handleEntitiesPageSelectAll(entityName, this, selectedEntities)}
      />
    ) : (
      <>
        <CustomButton
          fill
          pullRight
          bsSize="small"
          bsStyle="primary"
          className="inline-flex-center"
          hidden={pageConfig?.addButton?.hidden}
          onClick={() => history.push(pageConfig?.addButton?.linkTo || "/invoice")}
        >
          <i className="icn-plus icn-xs mr-5px" />
          {loc(pageConfig?.addButton?.label || "Add")}
        </CustomButton>

        <CustomButton
          bsStyle="primary"
          bsSize="small"
          pullRight
          loading={billingLoading}
          label={loc`Run billing`}
          onClick={() => this.setState({ showBillingModal: true })}
          hidden={pageConfig?.hideRunBillingButton}
        />
      </>
    )

    const data = this.formatTableData(filteredInvoices)

    return (
      <Grid className="invoices-page">
        {getEntitiesPageKpisCard({ pageConfig, contentName: `invoices-kpi` })}

        <Card title={title} action={action}>
          <Table
            useSearchRowComponent
            exportDataFileName={title}
            getEntities={this.getInvoices}
            searchFields={invoiceSearchFields}
            showRowsCount
            data={data}
            columns={columns}
            loading={loading}
            selectedEntities={selectedEntities}
            onRowClick={!pageConfig?.transitionsButton?.hidden && (async row => await handleEntitiesPageRowClick(entityName, this, row))}
            pinnedData={pinnedData}
            entityName={entityName}
            hasSearchResultsOnHiddenFields={hasSearchResultsOnHiddenFields}
            pageConfig={pageConfig}
            paginationParams={paginationParams}
          />

          {showBillingModal && (
            <Suspense fallback={null}>
              <BillingModal onClose={this.handleBillingModalClose} billingModalConfig={pageConfig?.billingModalConfig} />
            </Suspense>
          )}
        </Card>
      </Grid>
    )
  }
}

export default InvoicesPage

async function getPersons(search) {
  let persons = await searchEntities("person", search, { roles: "-SALES" })
  return getPayerPayeeValues(
    null,
    persons.map(it => ({ personRegistration: it.registration, person: it })),
    { withOrganizations: persons.length === 0, richValuesList: true },
  )
}

export const invoiceSearchFields = [
  [
    { field: "pr", select: "invoicePr", label: loc`Payable/Receivable`, colProps: { xs: 12, sm: 3 } },
    { field: "registration", colProps: { xs: 12, sm: 3 } },
    { field: "invoiceNumber", colProps: { xs: 12, sm: 3 } },
    { field: "name", regex: true, colProps: { xs: 12, sm: 3 } },
  ],
  [
    { field: "type", type: "multiple", select: "invoiceType", colProps: { xs: 12, sm: 3 } },
    { field: "status", type: "multiple", select: "invoiceStatus", colProps: { xs: 12, sm: 3 } },
    { field: "paymentMode", type: "multiple", select: "paymentMode", label: loc`Mode`, colProps: { xs: 12, sm: 3 } },
    { field: "paymentCondition", type: "multiple", select: "paymentCondition", colProps: { xs: 12, sm: 3 } },
  ],
  [
    { field: "currency", select: "currency", colProps: { xs: 12, sm: 3 } },
    { field: "internalReference", colProps: { xs: 12, sm: 3 } },
    { field: "externalReference", colProps: { xs: 12, sm: 3 } },
  ],
  [
    { field: "generationDate", type: "date", colProps: { xs: 12, sm: 3 } },
    { field: "issueDate", type: "date", colProps: { xs: 12, sm: 3 } },
    { field: "fromDueDate", type: "date", mustBeLessThan: "toDueDate", colProps: { xs: 12, sm: 3 } },
    { field: "toDueDate", type: "date", mustBeGreaterThan: "fromDueDate", colProps: { xs: 12, sm: 3 } },
  ],
  [
    {
      field: "contract",
      searchEntityName: "Contract",
      placeholder: "Type to search for a contract",
      actionHidden: true,
      regex: true,
      colProps: { xs: 12, sm: 3 },
    },
    { field: "payer", select: getPersons, placeholder: "Type to search for a payer", colProps: { xs: 12, sm: 3 } },
    { field: "payee", select: getPersons, placeholder: "Type to search for a payee", colProps: { xs: 12, sm: 3 } },
  ],
  [
    { field: "minAmountExclTax", label: loc`Amount (min)`, type: "currency", mustBeLessThan: "maxAmountExclTax", colProps: { xs: 12, sm: 3 } },
    { field: "maxAmountExclTax", label: loc`Amount (max)`, type: "currency", mustBeGreaterThan: "minAmountExclTax", colProps: { xs: 12, sm: 3 } },
  ],
]
